// The Player Databases tab: an arbiter's own player registries, the rating lists
// configured on them, and the history of tournaments reported into those lists.
//
// One module owns the whole feature because the three tabs share a loaded
// database and its cached player list; splitting them would mean re-fetching or
// passing that state around. The report-preview half is exported
// (openReportPreview) so the Standings view can open it without importing the
// rest of this file's DOM wiring.
//
// Data access is entirely in js/services/player-db.js; the Elo arithmetic is
// entirely in js/core/rating-engine.js (window.RatingEngine); every decision that
// needs no DOM — sorting, filtering, import parsing, what a report would move,
// what the rating-list form saves — is in js/utils/player-db-logic.js, under test.
// This file is rendering and event wiring only.

import { state, getActiveSettings } from '../state.js';
import { isOfflineError } from '../utils/offline-guards.js';
import { escapeHtml } from '../utils/formatting.js';
import * as PlayerDB from '../services/player-db.js';
import { syncTopHeader } from './top-header.js';
import {
    groupDatabases, ratingListsSummary, databaseCardSharing, toSelectableDatabases,
    ratingListName, resolveRatingColumn, playerRatingValue, visiblePlayers,
    nextSortState, sortIconClass, describeKBands, RL_DEFAULTS, suggestedNumberValue,
    parseKBand, buildRatingListConfig,
    validateDatabaseSettings, describeOpeningRating, buildOpeningRatings,
    openingRatingForPick, parseImportText, tournamentPlayersToRows, planImport,
    matchTournamentPlayers, buildEnginePlayers, mapGamesToDbIds, eventYearFrom,
    reportWarnings, formatSignedChange, decimalComma, describeReportError,
    sharingErrorMessage, sharingRows, classifyShareNotifications
} from '../utils/player-db-logic.js';
import { FUNCTIONS_BASE } from '../services/backend.js';

// ---------------------------------------------------------------------------
// Module state — what the page is currently showing.
// ---------------------------------------------------------------------------

let databases = [];
let activeDb = null;          // the full meta doc of the open database
let activePlayers = [];       // its players, from the IndexedDB-backed cache
let activeTab = 'players';
let playerFilter = '';
let ratingColumnListId = '';  // which list's rating the player table shows
let playerSort = { column: 'lastName', direction: 'asc' };
let editingPlayerId = null;
let editingListId = null;
let pendingReport = null;     // the preview awaiting confirmation

const $ = (id) => document.getElementById(id);

// Which database was open when the page was last closed, so a refresh comes back
// to it. Captured at module load and consumed ONCE, by the first render after
// the reload: reopening it on every later visit to the tab would fight the
// deliberate "coming back to this tab lands on the list" rule in
// renderDatabasesPage.
const OPEN_DB_KEY = 'openPlayerDatabaseId';

let pendingOpenDbId = readOpenDbId();

function readOpenDbId() {
    try {
        return localStorage.getItem(OPEN_DB_KEY) || '';
    } catch (e) {
        return '';
    }
}

function rememberOpenDb(dbId) {
    try {
        if (dbId) localStorage.setItem(OPEN_DB_KEY, String(dbId));
        else localStorage.removeItem(OPEN_DB_KEY);
    } catch (e) { /* localStorage may be unavailable */ }
}

function show(el, visible) {
    if (el) el.style.display = visible ? '' : 'none';
}

function openModal(id) {
    const m = $(id);
    if (m) m.classList.add('active');
}

function closeModal(id) {
    const m = $(id);
    if (m) m.classList.remove('active');
}

// A rating change is a signed number the eye should be able to scan quickly:
// always show the sign (formatSignedChange), colour it, and use the locale's
// decimal comma.
const CHANGE_COLOURS = { up: 'var(--success)', down: 'var(--danger)', none: 'var(--text-muted)' };

function formatChange(change) {
    const { text, tone } = formatSignedChange(change);
    return `<span style="color: ${CHANGE_COLOURS[tone]};">${text}</span>`;
}

// NOTE ON DATABASES WITH NO RATING LISTS. That is a supported way to use one, not
// a half-configured state: the database is a plain register of people, the roster
// searches it and players can be added back to it, and nothing is ever rated
// because there is no list to write a rating or a history entry to. Each renderer
// below hides its own rating machinery when `lists` is empty, rather than showing
// dead controls and a column of "-" that reads as data which failed to load.

// ---------------------------------------------------------------------------
// Database list pane
// ---------------------------------------------------------------------------

export async function renderDatabasesPage() {
    // Coming back from a detail view should land on the list, not on whatever
    // database happened to be open when the tab was last left.
    if (!activeDb) showListPane();
    try {
        databases = await PlayerDB.listMyDatabases();
    } catch (e) {
        console.error('Could not load player databases:', e);
        databases = [];
    }
    renderDatabaseCards();

    // First render after a reload: reopen whatever was open before it, unless
    // that database has since been deleted or shared away.
    if (pendingOpenDbId) {
        const dbId = pendingOpenDbId;
        pendingOpenDbId = '';
        if (databases.some(d => String(d.id) === String(dbId))) await openDatabase(dbId);
    }
}

function showListPane() {
    activeDb = null;
    activePlayers = [];
    rememberOpenDb(null);
    show($('db-list-pane'), true);
    show($('db-detail-pane'), false);
    // The two panes carry different page titles; the top panel shows whichever
    // is visible, and only re-checks when told to (js/ui/top-header.js).
    syncTopHeader();
}

function renderDatabaseCards() {
    const container = $('db-cards');
    if (!container) return;

    if (databases.length === 0) {
        container.innerHTML = `
            <div class="card glass" style="grid-column: 1 / -1; padding: 3rem; text-align: center; color: var(--text-muted);">
                <i class="ri-database-2-line" style="font-size: 2.5rem; display: block; margin-bottom: 1rem; opacity: 0.5;"></i>
                ${escapeHtml(tr('No player databases yet. Create one to keep your own players and rating lists.'))}
            </div>`;
        return;
    }

    const { owned, shared, invitations, withHeadings } = groupDatabases(databases);

    // Invitations first, because they want an answer.
    const groups = [
        { title: tr('Invitations'), items: invitations },
        { title: tr('My Databases'), items: owned },
        { title: tr('Shared with me'), items: shared }
    ].filter(g => g.items.length > 0);

    container.innerHTML = groups.map(g =>
        (withHeadings ? `<h3 class="db-registry-heading">${escapeHtml(g.title)}</h3>` : '') +
        g.items.map(renderDatabaseCard).join('')
    ).join('');
}

function renderDatabaseCard(d) {
    // An invitation is answered on its card and cannot be opened: until it is
    // accepted, the rules do not let the invitee read the players inside.
    if (d.role === 'pending') {
        return `
        <div class="card glass db-card db-card-pending" data-db-id="${escapeHtml(d.id)}">
            <div class="card-body" style="padding: 1.5rem;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;">
                    <h3 style="margin: 0 0 0.35rem; font-size: 1.15rem; color: var(--text-main);">${escapeHtml(d.name || tr('Untitled'))}</h3>
                    <span class="status-badge muted">${escapeHtml(tr('Invitation pending'))}</span>
                </div>
                <p style="margin: 0 0 1rem; color: var(--text-muted); font-size: 0.9rem;">${escapeHtml(tr('From {email}', { email: d.ownerEmail || '' }))}</p>
                <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
                    <button type="button" class="btn-primary" data-db-answer="accept"><i class="ri-check-line"></i> ${escapeHtml(tr('Accept'))}</button>
                    <button type="button" class="btn-outline" data-db-answer="decline">${escapeHtml(tr('Decline'))}</button>
                </div>
            </div>
        </div>`;
    }

    const lists = Array.isArray(d.lists) ? d.lists.length : 0;
    const badge = d.role === 'arbiter'
        ? `<span class="status-badge muted">${escapeHtml(tr('Shared with me'))}</span>`
        : `<span class="status-badge muted">${escapeHtml(tr('Owner'))}</span>`;
    // Who else is involved: whose it is, on a database shared WITH you; how many
    // arbiters it is shared with, on one of your own.
    const share = databaseCardSharing(d);
    const sharing = share.kind === ''
        ? ''
        : `<span><i class="${share.kind === 'owner' ? 'ri-user-star-line' : 'ri-user-shared-line'}"></i> ${escapeHtml(share.text)}</span>`;

    return `
        <div class="card glass db-card" data-db-id="${escapeHtml(d.id)}" style="cursor: pointer;">
            <div class="card-body" style="padding: 1.5rem;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;">
                    <h3 style="margin: 0 0 0.35rem; font-size: 1.15rem; color: var(--text-main);">${escapeHtml(d.name || tr('Untitled'))}</h3>
                    ${badge}
                </div>
                <p style="margin: 0 0 1rem; color: var(--text-muted); font-size: 0.9rem;">${escapeHtml(d.description || '')}</p>
                <div style="display: flex; gap: 1.5rem; flex-wrap: wrap; color: var(--text-muted); font-size: 0.85rem;">
                    <span><i class="ri-group-line"></i> ${d.playerCount || 0} ${escapeHtml(tr('players'))}</span>
                    <span><i class="ri-bar-chart-2-line"></i> ${escapeHtml(ratingListsSummary(lists))}</span>
                    ${sharing}
                </div>
            </div>
        </div>`;
}

// ---------------------------------------------------------------------------
// Database detail pane
// ---------------------------------------------------------------------------

async function openDatabase(dbId) {
    const doc = await PlayerDB.getDatabase(dbId);
    if (!doc) {
        window.Dialog.alert(tr('That database could not be opened.'), tr('Player Databases'));
        return;
    }
    activeDb = doc;
    rememberOpenDb(doc.id);
    show($('db-list-pane'), false);
    show($('db-detail-pane'), true);
    syncTopHeader();

    $('db-detail-name').textContent = doc.name || tr('Untitled');
    $('db-detail-subtitle').textContent = doc.description || '';

    selectTab('players');
    await refreshPlayers();
    renderRatingLists();
}

function selectTab(tab) {
    activeTab = tab;
    document.querySelectorAll('[data-db-tab]').forEach(btn => {
        btn.classList.toggle('active', btn.getAttribute('data-db-tab') === tab);
    });
    show($('db-tab-players'), tab === 'players');
    show($('db-tab-lists'), tab === 'lists');
    show($('db-tab-history'), tab === 'history');
    if (tab === 'history') renderEvents();
}

async function refreshPlayers({ force = false } = {}) {
    if (!activeDb) return;
    try {
        activePlayers = await PlayerDB.syncPlayers(activeDb.id, { force });
    } catch (e) {
        console.error('Could not sync players:', e);
        activePlayers = [];
    }
    renderRatingColumnPicker();
    renderPlayerTable();
}

function renderRatingColumnPicker() {
    const select = $('db-rating-column');
    if (!select) return;
    const lists = Array.isArray(activeDb && activeDb.lists) ? activeDb.lists : [];
    // With no lists there is nothing to pick between, so the control goes away
    // rather than offering a single dead option.
    const rated = lists.length > 0;
    // Not show(): that restores visibility with display:'', which would strip the
    // label's inline `display: flex` and leave the caption and the select on top
    // of each other instead of side by side.
    const pickerLabel = $('db-rating-column-label');
    if (pickerLabel) pickerLabel.style.display = rated ? 'flex' : 'none';
    const table = $('db-players-table');
    if (table) table.classList.toggle('no-rating-lists', !rated);

    select.innerHTML = lists.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name || l.id)}</option>`).join('');

    // The column this module thinks it is showing has to be the option the select
    // actually displays — see resolveRatingColumn() for why it used to drift.
    ratingColumnListId = resolveRatingColumn(lists, ratingColumnListId);
    select.value = ratingColumnListId;
}

function handleDbPlayerSort(column) {
    playerSort = nextSortState(playerSort, column);
    syncSortHeaders();
    renderPlayerTable();
}

function syncSortHeaders() {
    document.querySelectorAll('#db-player-table-header th.sortable').forEach(th => {
        const active = th.dataset.sort === playerSort.column;
        th.classList.toggle('active', active);
        const icon = th.querySelector('i');
        if (!icon) return;
        icon.className = sortIconClass(active, playerSort.direction);
    });
}

function renderPlayerTable() {
    const tbody = $('db-players-list');
    if (!tbody) return;

    const rows = visiblePlayers(activePlayers, {
        filter: playerFilter,
        sort: playerSort,
        ratingListId: ratingColumnListId
    });

    if (rows.length === 0) {
        tbody.innerHTML = `<tr><td colspan="9" style="text-align: center; padding: 3rem; color: var(--text-muted);">${
            escapeHtml(activePlayers.length === 0
                ? tr('No players yet. Add one, or import a list.')
                : tr('No players match that search.'))
        }</td></tr>`;
        return;
    }

    tbody.innerHTML = rows.map(p => {
        const value = playerRatingValue(p, ratingColumnListId);
        const entry = (p.ratings || {})[ratingColumnListId];
        const games = entry && typeof entry === 'object' ? (entry.games || 0) : 0;
        return `
        <tr data-player-id="${escapeHtml(p.id)}"${p.active === false ? ' style="opacity: 0.55;"' : ''}>
            <td><strong>${escapeHtml(p.lastName || p.name || '')}</strong></td>
            <td>${escapeHtml(p.givenName || '')}</td>
            <td>${escapeHtml(p.title || '')}</td>
            <td>${escapeHtml(p.club || '')}</td>
            <td>${escapeHtml(p.fed || '')}</td>
            <td>${escapeHtml(p.birthYear || '')}</td>
            <td class="col-db-rating">${value ? value : '<span style="color: var(--text-muted);">-</span>'}</td>
            <td class="col-db-games">${games || '<span style="color: var(--text-muted);">-</span>'}</td>
            <td>
                <button class="btn-icon-small db-player-edit" title="${escapeHtml(tr('Edit'))}"><i class="ri-pencil-line"></i></button>
                <button class="btn-icon-small db-player-delete" title="${escapeHtml(tr('Remove'))}"><i class="ri-delete-bin-line"></i></button>
            </td>
        </tr>`;
    }).join('');
}

// ---------------------------------------------------------------------------
// Rating lists tab
// ---------------------------------------------------------------------------

function renderRatingLists() {
    const container = $('db-lists-container');
    if (!container) return;
    const lists = Array.isArray(activeDb && activeDb.lists) ? activeDb.lists : [];

    if (lists.length === 0) {
        container.innerHTML = `
            <div class="card glass" style="padding: 2.5rem; text-align: center; color: var(--text-muted);">
                ${escapeHtml(tr('No rating lists. A database works without one — it keeps your players, and the roster can search it, but nothing is rated. Add a list only if you want this database to calculate and record ratings.'))}
            </div>`;
        return;
    }

    container.innerHTML = lists.map(l => {
        const cfg = window.RatingEngine.normalizeListConfig(l);
        const bands = describeKBands(cfg.kTable);
        return `
        <div class="card glass" data-list-id="${escapeHtml(l.id)}">
            <div class="card-body" style="padding: 1.25rem 1.5rem; display: flex; justify-content: space-between; align-items: center; gap: 1.5rem; flex-wrap: wrap;">
                <div>
                    <h3 style="margin: 0 0 0.35rem; font-size: 1.05rem; color: var(--text-main);">${escapeHtml(l.name || l.id)}</h3>
                    <div style="color: var(--text-muted); font-size: 0.85rem; display: flex; gap: 1.25rem; flex-wrap: wrap;">
                        <span>${escapeHtml(tr('Start'))}: ${cfg.startRating}</span>
                        <span>${escapeHtml(bands)}</span>
                        ${cfg.maxChangePerEvent ? `<span>${escapeHtml(tr('Max'))}: ±${cfg.maxChangePerEvent}</span>` : ''}
                    </div>
                </div>
                <button class="btn-outline db-list-edit"><i class="ri-settings-4-line"></i> ${escapeHtml(tr('Configure'))}</button>
            </div>
        </div>`;
    }).join('');
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

async function renderEvents() {
    const container = $('db-events-container');
    if (!container || !activeDb) return;
    container.innerHTML = `<div style="color: var(--text-muted);">${escapeHtml(tr('Loading...'))}</div>`;

    let events = [];
    try {
        events = await PlayerDB.listEvents(activeDb.id);
    } catch (e) {
        console.error('Could not load rating history:', e);
    }

    if (events.length === 0) {
        container.innerHTML = `
            <div class="card glass" style="padding: 2.5rem; text-align: center; color: var(--text-muted);">
                ${escapeHtml(tr('Nothing reported yet. Finish a tournament bound to one of these lists and report it.'))}
            </div>`;
        return;
    }

    container.innerHTML = events.map(ev => {
        const when = ev.appliedAt ? new Date(ev.appliedAt).toLocaleString() : '';
        const count = Array.isArray(ev.deltas) ? ev.deltas.length : 0;
        return `
        <div class="card glass" data-event-id="${escapeHtml(ev.id)}"${ev.reverted ? ' style="opacity: 0.6;"' : ''}>
            <div class="card-body" style="padding: 1.25rem 1.5rem; display: flex; justify-content: space-between; align-items: center; gap: 1.5rem; flex-wrap: wrap;">
                <div>
                    <h3 style="margin: 0 0 0.35rem; font-size: 1.05rem; color: var(--text-main);">
                        ${escapeHtml(ev.tournamentName || tr('Tournament'))}
                        ${ev.reverted ? `<span class="status-badge muted" style="margin-left: 0.5rem;">${escapeHtml(tr('Reverted'))}</span>` : ''}
                    </h3>
                    <div style="color: var(--text-muted); font-size: 0.85rem; display: flex; gap: 1.25rem; flex-wrap: wrap;">
                        <span>${escapeHtml(ev.listName || ratingListName(activeDb, ev.listId))}</span>
                        <span>${count} ${escapeHtml(tr('players'))}</span>
                        <span>${escapeHtml(when)}</span>
                        <span>${escapeHtml(ev.appliedByEmail || '')}</span>
                    </div>
                </div>
                ${ev.reverted ? '' : `<button class="btn-outline db-event-revert"><i class="ri-arrow-go-back-line"></i> ${escapeHtml(tr('Revert'))}</button>`}
            </div>
        </div>`;
    }).join('');
}

async function revertEvent(eventId) {
    const confirmed = await window.Dialog.confirm(
        tr('This subtracts the ratings this tournament gave. It only works if nothing has been reported for these players since.'),
        tr('Revert Report'), tr('Revert'), false, undefined, 'warning'
    );
    if (!confirmed) return;

    const result = await callFunction('revertRatingReport', { dbId: activeDb.id, eventId });
    if (!result.ok) {
        window.Dialog.error(describeReportError(result), tr('Could Not Revert'));
        return;
    }
    await refreshPlayers({ force: true });
    await renderEvents();
}

// ---------------------------------------------------------------------------
// Cloud Function calls
// ---------------------------------------------------------------------------

async function callFunction(name, body) {
    const token = window.authApp && window.authApp.getIdToken ? await window.authApp.getIdToken() : null;
    if (!token) return { ok: false, error: 'not-signed-in' };

    try {
        const res = await fetch(`${FUNCTIONS_BASE}/${name}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => ({}));
        return res.ok ? { ok: true, data } : { ok: false, status: res.status, ...data };
    } catch (e) {
        console.error(`${name} failed:`, e);
        return { ok: false, error: 'network' };
    }
}

// ---------------------------------------------------------------------------
// Database settings modal
// ---------------------------------------------------------------------------

function openDatabaseSettings(dbDoc) {
    const isNew = !dbDoc;
    $('db-settings-title').textContent = isNew ? tr('New Database') : tr('Database Settings');
    $('db-settings-name').value = isNew ? '' : (dbDoc.name || '');
    $('db-settings-description').value = isNew ? '' : (dbDoc.description || '');
    // Deleting is the owner's call alone, and only for a database that exists.
    show($('db-settings-danger'), !isNew && dbDoc.ownerId === currentUid());
    // Sharing needs a database to point an invitation at, so a new one is saved first.
    show($('db-sharing-section'), !isNew);
    if (!isNew) renderSharingSection(dbDoc);
    const inviteInput = $('db-invite-email');
    if (inviteInput) inviteInput.value = '';
    openModal('db-settings-modal');
}

// Who a database is shared with, and - for its owner - the controls to change
// that. Mirrors the Cooperating Arbiters list in the tournament settings.
function renderSharingSection(dbDoc) {
    const listEl = $('db-arbiters-list');
    if (!listEl) return;
    const isOwner = dbDoc.ownerId === currentUid();

    show($('db-invite-controls'), isOwner);
    $('db-sharing-intro').textContent = isOwner
        ? tr('Invite other registered arbiters to use this database. They get a notification, and the database appears in their registry once they accept.')
        : tr('Shared with you by {owner}. Only the owner can change who it is shared with.', { owner: dbDoc.ownerEmail || '' });

    const badge = (text, cls) =>
        `<span class="status-badge ${cls}" style="font-size: 0.7rem; padding: 0.2rem 0.4rem;">${escapeHtml(text)}</span>`;
    const row = (email, icon, iconColour, badgeHtml, actionHtml, muted) => `
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; padding: 0.35rem 0.5rem;">
            <span style="font-size: 0.85rem; color: ${muted ? 'var(--text-muted)' : 'var(--text-main)'}; overflow-wrap: anywhere;"><i class="${icon}" style="color: ${iconColour}; margin-right: 0.25rem;"></i> ${escapeHtml(email)}</span>
            <div style="display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0;">${badgeHtml}${actionHtml}</div>
        </div>`;

    // An arbiter looking at someone else's database is told whose it is; only the
    // owner gets the buttons (sharingRows decides both).
    const LOOK = {
        owner: { icon: 'ri-user-star-line', colour: 'var(--primary)', badge: [tr('Owner'), 'upcoming'] },
        arbiter: { icon: 'ri-user-follow-line', colour: 'var(--success)', badge: [tr('Co-Arbiter'), 'active'] },
        pending: { icon: 'ri-mail-send-line', colour: 'var(--primary)', badge: [tr('Pending'), 'muted'] }
    };
    const ACTION = {
        remove: (email) => `<button type="button" class="btn-icon-small btn-icon-danger" data-db-remove="${escapeHtml(email)}" title="${escapeHtml(tr('Stop sharing'))}"><i class="ri-user-unfollow-line"></i></button>`,
        retract: (email) => `<button type="button" class="btn-icon-small btn-icon-danger" data-db-retract="${escapeHtml(email)}" title="${escapeHtml(tr('Retract invitation'))}"><i class="ri-close-circle-line"></i></button>`
    };

    const html = sharingRows(dbDoc, isOwner).map(r => {
        const look = LOOK[r.kind];
        return row(r.email, look.icon, look.colour, badge(look.badge[0], look.badge[1]),
            r.action ? ACTION[r.action](r.email) : '', r.muted);
    }).join('');

    listEl.innerHTML = html
        || `<p style="font-size: 0.8rem; color: var(--text-muted); font-style: italic; text-align: center; margin: 0.5rem 0;">${escapeHtml(tr('Not shared with anyone yet.'))}</p>`;
}

// Re-read the open database after a sharing change, so the list shows what the
// server now holds rather than what we assume we wrote.
async function refreshSharing() {
    if (!activeDb) return;
    const fresh = await PlayerDB.getDatabase(activeDb.id);
    if (fresh) {
        activeDb = { ...activeDb, ...fresh };
        renderSharingSection(activeDb);
    }
}

async function inviteToDatabase() {
    if (!activeDb) return;
    const input = $('db-invite-email');
    const btn = $('btn-db-invite');
    const email = (input.value || '').trim();
    if (!email) {
        window.Dialog.error(tr('Please enter a valid email address.'), tr('Error'));
        return;
    }
    btn.disabled = true;
    try {
        const invited = await PlayerDB.inviteDatabaseArbiter(activeDb.id, email);
        input.value = '';
        await refreshSharing();
        window.Dialog.success(tr('Invitation sent to {email}.', { email: invited }), tr('Success'));
    } catch (err) {
        console.error('Could not share the database:', err);
        window.Dialog.error(sharingErrorMessage(err), tr('Error'));
    } finally {
        btn.disabled = false;
    }
}

async function onSharingListClick(e) {
    if (!activeDb) return;
    const retract = e.target.closest('[data-db-retract]');
    const remove = e.target.closest('[data-db-remove]');
    const btn = retract || remove;
    if (!btn) return;
    const email = btn.getAttribute(retract ? 'data-db-retract' : 'data-db-remove');

    const confirmed = retract
        ? await window.Dialog.confirm(
            tr('Retract the invitation to {email}?', { email }),
            tr('Retract Invitation'), tr('Yes, Retract'), true, tr('Keep It'))
        : await window.Dialog.confirm(
            tr('Stop sharing this database with {email}? They lose access immediately and are told about it.', { email }),
            tr('Stop sharing'), tr('Yes, Remove'), true, tr('Cancel'));
    if (!confirmed) return;

    btn.disabled = true;
    try {
        if (retract) await PlayerDB.retractDatabaseInvite(activeDb.id, email);
        else await PlayerDB.removeDatabaseArbiter(activeDb.id, email);
        await refreshSharing();
    } catch (err) {
        console.error('Could not change who the database is shared with:', err);
        window.Dialog.error(sharingErrorMessage(err), tr('Error'));
        btn.disabled = false;
    }
}

function currentUid() {
    const user = window.authApp && window.authApp.getCurrentUser ? window.authApp.getCurrentUser() : null;
    return user ? user.uid : null;
}

async function saveDatabaseSettings(e) {
    e.preventDefault();
    const { valid, patch } = validateDatabaseSettings({
        name: $('db-settings-name').value,
        description: $('db-settings-description').value
    });
    if (!valid) return;

    try {
        if (activeDb) {
            await PlayerDB.updateDatabase(activeDb.id, patch);
            activeDb = { ...activeDb, ...patch };
            $('db-detail-name').textContent = patch.name;
            $('db-detail-subtitle').textContent = patch.description;
        } else {
            const created = await PlayerDB.createDatabase(patch);
            closeModal('db-settings-modal');
            await renderDatabasesPage();
            await openDatabase(created.id);
            return;
        }
        closeModal('db-settings-modal');
    } catch (err) {
        console.error('Could not save database:', err);
        window.Dialog.alert(tr('Could not save the database.'), tr('Player Databases'));
    }
}

async function deleteActiveDatabase() {
    if (!activeDb) return;
    const confirmed = await window.Dialog.confirm(
        tr('Delete "{name}" with all its players and rating history? This cannot be undone.', { name: activeDb.name || '' }),
        tr('Delete Database'), tr('Delete'), true
    );
    if (!confirmed) return;

    try {
        await PlayerDB.deleteDatabase(activeDb.id);
        closeModal('db-settings-modal');
        showListPane();
        await renderDatabasesPage();
    } catch (e) {
        console.error('Could not delete database:', e);
        window.Dialog.alert(
            isOfflineError(e)
                ? tr('Deleting a database needs an internet connection. Nothing more was deleted — try again when you are online.')
                : tr('Could not delete the database.'),
            tr('Player Databases'));
    }
}

// ---------------------------------------------------------------------------
// Player modal
// ---------------------------------------------------------------------------

function openPlayerModal(player) {
    editingPlayerId = player ? player.id : null;
    $('db-player-modal-title').textContent = player ? tr('Edit Player') : tr('Add Player');

    const p = player || {};
    $('db-player-given-name').value = p.givenName || '';
    $('db-player-last-name').value = p.lastName || '';
    $('db-player-title').value = p.title || '';
    $('db-player-club').value = p.club || '';
    $('db-player-fed').value = p.fed || '';
    $('db-player-birthyear').value = p.birthYear || '';
    $('db-player-sex').value = p.sex || '';
    $('db-player-fide-id').value = p.fideId || '';
    $('db-player-nsf-id').value = p.nsfId || '';
    $('db-player-member-no').value = p.memberNo || '';
    $('db-player-active').checked = p.active !== false;

    // Opening ratings can be set for any list the player is not yet rated in —
    // on create, and afterwards for anyone who was added before a list existed.
    // Lists they ARE rated in are shown read-only; those move only by reporting.
    renderOpeningRatingInputs(player);

    $('db-player-fide-search').value = '';
    $('db-player-fide-results').classList.add('hidden');
    openModal('db-player-modal');
}

function renderOpeningRatingInputs(player) {
    const container = $('db-player-ratings-rows');
    if (!container) return;
    const lists = Array.isArray(activeDb && activeDb.lists) ? activeDb.lists : [];

    // Nothing to open a rating in: the whole section goes, rather than announcing
    // an absence the arbiter did not ask about. This is the normal state of a
    // database used purely as a register of players.
    show($('db-player-opening-ratings'), lists.length > 0);
    if (lists.length === 0) {
        container.innerHTML = '';
        return;
    }

    const existing = (player && player.ratings) || {};

    container.innerHTML = lists.map(l => {
        const { label, rated, games } = describeOpeningRating(l, existing);

        // Already rated: show it, but not as something editable — a rating that
        // exists moves only by reporting a tournament (or reverting one).
        if (rated) {
            return `
            <div class="form-group">
                <label>${escapeHtml(label)}</label>
                <input type="number" value="${rated}" disabled
                       title="${escapeHtml(tr('Set by reported tournaments'))}">
                <p class="page-subtitle" style="margin: 0.3rem 0 0; font-size: 0.78rem;">
                    ${escapeHtml(tr('{games} games played', { games }))}
                </p>
            </div>`;
        }

        // Not rated yet: offer the list's start rating as the default, which is
        // what "start rating" means to the arbiter who set it. Clearing the box
        // deliberately leaves the player unrated.
        const cfg = window.RatingEngine.normalizeListConfig(l);
        return `
        <div class="form-group">
            <label for="db-opening-${escapeHtml(l.id)}">${escapeHtml(l.name || l.id)}</label>
            <input type="number" id="db-opening-${escapeHtml(l.id)}" data-list-id="${escapeHtml(l.id)}"
                   class="db-opening-rating" value="${cfg.startRating}"
                   data-start-rating="${cfg.startRating}"
                   placeholder="${escapeHtml(tr('Unrated'))}">
            <p class="page-subtitle" style="margin: 0.3rem 0 0; font-size: 0.78rem;">
                ${escapeHtml(tr('Start rating for this list. Clear the box to leave them unrated.'))}
            </p>
        </div>`;
    }).join('');
}

// Only the enabled inputs are collected, so a list the player is already rated in
// can never be included — its box is disabled and carries no data-list-id.
function collectOpeningRatings() {
    return buildOpeningRatings(Array.from(document.querySelectorAll('.db-opening-rating'))
        .map(input => ({ listId: input.getAttribute('data-list-id'), value: input.value })));
}

async function savePlayer(e) {
    e.preventDefault();
    if (!activeDb) return;

    const player = {
        givenName: $('db-player-given-name').value,
        lastName: $('db-player-last-name').value,
        title: $('db-player-title').value,
        club: $('db-player-club').value,
        fed: $('db-player-fed').value,
        birthYear: $('db-player-birthyear').value,
        sex: $('db-player-sex').value,
        fideId: $('db-player-fide-id').value,
        nsfId: $('db-player-nsf-id').value,
        memberNo: $('db-player-member-no').value,
        active: $('db-player-active').checked
    };
    if (!player.lastName.trim()) return;

    try {
        if (editingPlayerId) {
            // addRatings only ever carries lists this player is currently unrated
            // in; the service merges it over their existing map and drops anything
            // that would overwrite (matching what the rules allow).
            await PlayerDB.updatePlayer(activeDb.id, editingPlayerId, player, {
                addRatings: collectOpeningRatings()
            });
        } else {
            await PlayerDB.createPlayer(activeDb.id, { ...player, ratings: collectOpeningRatings() });
            await bumpPlayerCount(1);
        }
        closeModal('db-player-modal');
        await refreshPlayers();
    } catch (err) {
        console.error('Could not save player:', err);
        window.Dialog.alert(tr('Could not save the player.'), tr('Player Databases'));
    }
}

async function removePlayer(playerId) {
    const player = activePlayers.find(p => String(p.id) === String(playerId));
    const confirmed = await window.Dialog.confirm(
        tr('Remove {name} from this database? Their rating history stays in the log.', { name: (player && player.name) || '' }),
        tr('Remove Player'), tr('Remove'), true
    );
    if (!confirmed) return;

    try {
        await PlayerDB.deletePlayer(activeDb.id, playerId);
        await bumpPlayerCount(-1);
        await refreshPlayers();
    } catch (e) {
        console.error('Could not remove player:', e);
    }
}

// The card on the list pane shows a player count. It is a denormalised
// convenience, not a source of truth, so a failed update is not worth surfacing.
async function bumpPlayerCount(delta) {
    if (!activeDb) return;
    const next = Math.max(0, (activeDb.playerCount || 0) + delta);
    activeDb.playerCount = next;
    try {
        await PlayerDB.updateDatabase(activeDb.id, { playerCount: next });
    } catch (e) { /* cosmetic only */ }
}

// ---------------------------------------------------------------------------
// FIDE search inside the database player modal
//
// Hits the public searchFide endpoint directly rather than going through
// js/workers/rating-worker.js: that worker is wired to the two tournament search
// boxes and their result rendering, and this dialog wants the raw player to
// prefill a FORM, not to add someone to a tournament.
// ---------------------------------------------------------------------------

let fideSearchTimer = null;

function wireFideSearch() {
    const input = $('db-player-fide-search');
    const results = $('db-player-fide-results');
    const spinner = $('db-player-fide-spinner');
    if (!input || !results) return;

    input.addEventListener('input', () => {
        const q = input.value.trim();
        clearTimeout(fideSearchTimer);
        if (q.length < 3) {
            results.classList.add('hidden');
            spinner.classList.add('hidden');
            return;
        }
        spinner.classList.remove('hidden');
        fideSearchTimer = setTimeout(() => runFideSearch(q), 300);
    });

    results.addEventListener('click', (e) => {
        const item = e.target.closest('[data-fide-index]');
        if (!item) return;
        const player = lastFideResults[parseInt(item.getAttribute('data-fide-index'), 10)];
        if (player) fillPlayerFormFromFide(player);
        results.classList.add('hidden');
        input.value = '';
    });
}

let lastFideResults = [];

async function runFideSearch(q) {
    const results = $('db-player-fide-results');
    const spinner = $('db-player-fide-spinner');
    try {
        const res = await fetch(`${FUNCTIONS_BASE}/searchFide?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        lastFideResults = Array.isArray(data.results) ? data.results : [];
    } catch (e) {
        lastFideResults = [];
    }
    spinner.classList.add('hidden');

    if (lastFideResults.length === 0) {
        results.innerHTML = `<div class="search-result-item">${escapeHtml(tr('No players found'))}</div>`;
    } else {
        results.innerHTML = lastFideResults.map((p, i) => `
            <div class="search-result-item" data-fide-index="${i}">
                <div class="search-result-header">
                    <span class="search-result-name">${escapeHtml(p.name)}</span>
                    <span class="search-result-rating">${escapeHtml(String(p.standardRating || 0))}</span>
                </div>
                <div class="search-result-details">
                    <span><i class="ri-flag-line"></i> ${escapeHtml(p.fed || '')}</span>
                    <span><i class="ri-medal-line"></i> ${escapeHtml(p.title || tr('No Title'))}</span>
                    <span>ID: ${escapeHtml(p.fideId || '')}</span>
                </div>
            </div>`).join('');
    }
    results.classList.remove('hidden');
}

function fillPlayerFormFromFide(p) {
    $('db-player-given-name').value = p.givenName || '';
    $('db-player-last-name').value = p.lastName || p.name || '';
    $('db-player-title').value = p.title || '';
    $('db-player-fed').value = p.fed || '';
    $('db-player-birthyear').value = p.birthYear || '';
    $('db-player-fide-id').value = p.fideId || '';

    // Someone picked out of the FIDE list arrives with a rating, so the opening
    // boxes offer the highest one they hold instead of the list's start rating:
    // a titled player must not enter the club list as a beginner. Only the boxes
    // for lists they are unrated in exist here, and the arbiter can still edit
    // or clear them before saving.
    //
    // Written on EVERY pick, including one with no rating at all. It used to skip
    // the unrated case, which left whatever the previous pick had put there: search
    // a name, pick the wrong person, search again and pick the right — unrated —
    // one, and they were saved carrying a stranger's rating instead of the list's
    // start rating. Each box restores its own list's start rating, since two lists
    // need not start at the same number.
    document.querySelectorAll('.db-opening-rating').forEach(input => {
        input.value = openingRatingForPick(p, input.dataset.startRating);
    });
}

// ---------------------------------------------------------------------------
// Rating list configuration modal
// ---------------------------------------------------------------------------

// Numbers whose default IS the suggestion are left BLANK, with the default shown
// as the input's grey placeholder (see suggestedNumberValue, and RL_DEFAULTS for
// what "not set" means in each box).
function setSuggestedNumber(id, value, suggestion) {
    const el = $(id);
    if (!el) return;
    el.value = suggestedNumberValue(value, suggestion);
}

function openRatingListModal(listId) {
    editingListId = listId || null;
    const existing = listId ? PlayerDB.findRatingList(activeDb, listId) : null;
    const cfg = window.RatingEngine.normalizeListConfig(existing || {});

    $('rating-list-modal-title').textContent = existing ? tr('Rating List') : tr('New Rating List');
    $('rl-name').value = existing ? (existing.name || '') : '';
    setSuggestedNumber('rl-start-rating', cfg.startRating, RL_DEFAULTS.startRating);
    $('rl-k-provisional').value = cfg.kProvisional === null ? '' : cfg.kProvisional;
    setSuggestedNumber('rl-provisional-games', cfg.provisional.games, RL_DEFAULTS.provisionalGames);
    $('rl-provisional-mode').value = cfg.provisional.mode;
    $('rl-k-junior').value = cfg.kJunior === null ? '' : cfg.kJunior;
    setSuggestedNumber('rl-junior-age', cfg.juniorMaxAge, RL_DEFAULTS.juniorMaxAge);
    setSuggestedNumber('rl-max-change', cfg.maxChangePerEvent, RL_DEFAULTS.maxChangePerEvent);
    $('rl-rounding').value = cfg.rounding;
    $('rl-expected-model').value = cfg.expectedScoreModel;
    $('rl-unrated-opponents').value = cfg.unratedOpponents;
    $('rl-seed-unrated').value = cfg.seedUnrated;
    $('rl-use-400').checked = cfg.use400Rule;
    $('rl-count-forfeits').checked = cfg.countForfeits;

    renderKBands(cfg.kTable);
    show($('btn-rl-delete'), !!existing);
    openModal('rating-list-modal');
}

function renderKBands(bands) {
    const container = $('rl-k-rows');
    if (!container) return;
    container.innerHTML = bands.map(b => kBandRow(b.maxRating, b.k)).join('');
}

function kBandRow(maxRating, k) {
    return `
    <div class="rl-k-row" style="display: flex; gap: 0.75rem; align-items: center;">
        <span style="color: var(--text-muted); font-size: 0.85rem; white-space: nowrap;">${escapeHtml(tr('Rating up to'))}</span>
        <input type="number" class="rl-k-max" value="${maxRating}" style="max-width: 120px;">
        <span style="color: var(--text-muted); font-size: 0.85rem;">K =</span>
        <input type="number" class="rl-k-value" value="${k}" style="max-width: 100px;">
        <button type="button" class="btn-icon-small rl-k-remove" title="${escapeHtml(tr('Remove'))}"><i class="ri-close-line"></i></button>
    </div>`;
}

function collectKBands() {
    return Array.from(document.querySelectorAll('.rl-k-row')).map(row =>
        parseKBand(row.querySelector('.rl-k-max').value, row.querySelector('.rl-k-value').value));
}

async function saveRatingList(e) {
    e.preventDefault();
    if (!activeDb) return;

    // The list as currently stored, so buildRatingListConfig can carry forward the
    // fields this form no longer has inputs for.
    const previous = editingListId ? PlayerDB.findRatingList(activeDb, editingListId) : null;

    const cfg = buildRatingListConfig({
        id: editingListId,
        name: $('rl-name').value,
        startRating: $('rl-start-rating').value,
        kTable: collectKBands(),
        kProvisional: $('rl-k-provisional').value,
        provisionalGames: $('rl-provisional-games').value,
        provisionalMode: $('rl-provisional-mode').value,
        kJunior: $('rl-k-junior').value,
        juniorMaxAge: $('rl-junior-age').value,
        maxChangePerEvent: $('rl-max-change').value,
        rounding: $('rl-rounding').value,
        expectedScoreModel: $('rl-expected-model').value,
        unratedOpponents: $('rl-unrated-opponents').value,
        seedUnrated: $('rl-seed-unrated').value,
        use400Rule: $('rl-use-400').checked,
        countForfeits: $('rl-count-forfeits').checked
    }, previous);
    if (!cfg.name) return;

    try {
        await PlayerDB.saveRatingList(activeDb.id, cfg);
        activeDb = await PlayerDB.getDatabase(activeDb.id);
        closeModal('rating-list-modal');
        renderRatingLists();
        renderRatingColumnPicker();
        renderPlayerTable();
    } catch (err) {
        console.error('Could not save rating list:', err);
        window.Dialog.alert(tr('Could not save the rating list.'), tr('Player Databases'));
    }
}

async function deleteRatingListConfig() {
    if (!activeDb || !editingListId) return;
    const confirmed = await window.Dialog.confirm(
        tr('Delete this rating list? The ratings players already hold are kept, so re-creating the list restores them.'),
        tr('Delete Rating List'), tr('Delete'), true
    );
    if (!confirmed) return;

    await PlayerDB.deleteRatingList(activeDb.id, editingListId);
    activeDb = await PlayerDB.getDatabase(activeDb.id);
    closeModal('rating-list-modal');
    renderRatingLists();
    renderRatingColumnPicker();
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

function openImportModal() {
    const select = $('db-import-list');
    const lists = Array.isArray(activeDb && activeDb.lists) ? activeDb.lists : [];
    show($('db-import-list-group'), lists.length > 0);
    select.innerHTML = `<option value="">${escapeHtml(tr('No rating (leave unrated)'))}</option>` +
        lists.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name || l.id)}</option>`).join('');
    $('db-import-csv').value = '';
    $('db-import-preview').innerHTML = '';
    syncImportSourceVisibility();
    openModal('db-import-modal');
}

function syncImportSourceVisibility() {
    show($('db-import-csv-group'), $('db-import-source').value === 'csv');
}

function collectImportRows() {
    if ($('db-import-source').value === 'csv') return parseImportText($('db-import-csv').value);
    return tournamentPlayersToRows(state.players || []);
}

async function runImport() {
    if (!activeDb) return;
    const rows = collectImportRows();
    const listId = $('db-import-list').value;
    const preview = $('db-import-preview');

    if (rows.length === 0) {
        preview.innerHTML = `<p style="color: var(--danger);">${escapeHtml(tr('Nothing to import.'))}</p>`;
        return;
    }

    // Who is already in there, and what each row's opening rating would be, before
    // a single write: planImport() holds both rules (skip a duplicate name, fall
    // back to the list's start rating).
    const listCfg = listId
        ? window.RatingEngine.normalizeListConfig(PlayerDB.findRatingList(activeDb, listId) || {})
        : null;
    const plan = planImport(rows, activePlayers, {
        listId: listCfg ? listId : '',
        startRating: listCfg ? listCfg.startRating : 0
    });

    let added = 0;
    let skipped = plan.skipped;

    preview.innerHTML = `<p style="color: var(--text-muted);">${escapeHtml(tr('Importing...'))}</p>`;

    for (const player of plan.creates) {
        try {
            await PlayerDB.createPlayer(activeDb.id, player);
            added++;
        } catch (e) {
            console.error('Could not import player:', player, e);
            skipped++;
        }
    }

    await bumpPlayerCount(added);
    preview.innerHTML = `<p style="color: var(--success);">${
        escapeHtml(tr('Imported {added} players ({skipped} skipped).', { added, skipped }))}</p>`;
    await refreshPlayers();
}

// ---------------------------------------------------------------------------
// Report preview — opened from the Standings view.
// ---------------------------------------------------------------------------

/**
 * Compute and show what reporting the ACTIVE tournament group would do to its
 * bound rating list. Nothing is written until the arbiter confirms.
 *
 * Players are matched to database records by FIDE id first, then by name — an id
 * is unambiguous, a name is the fallback for the club players who have no id
 * (which is the entire reason this feature exists).
 */
export async function openReportPreview() {
    const settings = getActiveSettings();
    if (!settings.ratingDbId || !settings.ratingListId) {
        window.Dialog.alert(tr('This group is not bound to a rating list. Choose one in the tournament settings first.'), tr('Report to Rating List'));
        return;
    }

    const database = await PlayerDB.getDatabase(settings.ratingDbId);
    const list = PlayerDB.findRatingList(database, settings.ratingListId);
    if (!database || !list) {
        window.Dialog.alert(tr('That rating list no longer exists.'), tr('Report to Rating List'));
        return;
    }

    const dbPlayers = await PlayerDB.syncPlayers(database.id);
    previewPlayers = dbPlayers; // so the preview can name players of a database that is not open

    // The engine is fed DATABASE ids, so the deltas it produces address database
    // documents directly and no second mapping is needed when applying.
    const { matched, unmatched, shared, idMap } = matchTournamentPlayers(state.players || [], dbPlayers);
    const enginePlayers = buildEnginePlayers(matched, list.id);
    const games = mapGamesToDbIds(window.RatingEngine.gamesFromRounds(state.rounds || []), idMap);

    const eventYear = eventYearFrom(state.activeTournament && state.activeTournament.startDate);
    const report = window.RatingEngine.computeReport(list, { players: enginePlayers, games }, { eventYear });

    pendingReport = {
        dbId: database.id,
        listId: list.id,
        eventId: PlayerDB.eventIdFor(
            (state.activeTournament && state.activeTournament.id) || '',
            state.activeGroupId || '',
            list.id
        ),
        tournamentId: (state.activeTournament && state.activeTournament.id) || '',
        groupId: state.activeGroupId || '',
        tournamentName: (state.activeTournament && state.activeTournament.name) || '',
        endDate: (state.activeTournament && state.activeTournament.endDate) || '',
        deltas: report.deltas
    };

    renderReportPreview(report, database, list, unmatched, shared);
    openModal('report-preview-modal');
}

function renderReportPreview(report, database, list, unmatched, shared) {
    const nameOf = (playerId) => {
        const p = activePlayersFor(database).find(x => String(x.id) === String(playerId));
        return p ? p.name : playerId;
    };

    $('report-preview-title').textContent = tr('Report to {list}', { list: list.name || list.id });
    $('report-preview-summary').innerHTML = `
        <p class="page-subtitle" style="margin: 0;">
            ${escapeHtml(tr('{count} players in {database}. Nothing is saved until you confirm.', {
                count: report.deltas.length, database: database.name || ''
            }))}
        </p>`;

    const warnings = reportWarnings(unmatched, report, shared);

    $('report-preview-warnings').innerHTML = warnings.length === 0 ? '' : `
        <div class="card glass" style="padding: 1rem 1.25rem; border-left: 3px solid var(--warning, var(--primary));">
            ${warnings.map(w => `<p style="margin: 0.25rem 0; color: var(--text-muted); font-size: 0.9rem;">${escapeHtml(w)}</p>`).join('')}
        </div>`;

    const tbody = $('report-preview-rows');
    if (report.deltas.length === 0) {
        tbody.innerHTML = `<tr><td colspan="8" style="text-align: center; padding: 2.5rem; color: var(--text-muted);">${
            escapeHtml(tr('Nothing to report — no rated games between players in this database.'))}</td></tr>`;
        $('btn-report-apply').setAttribute('disabled', 'disabled');
        return;
    }
    $('btn-report-apply').removeAttribute('disabled');

    tbody.innerHTML = report.deltas.map(d => `
        <tr>
            <td><strong>${escapeHtml(nameOf(d.playerId))}</strong></td>
            <td>${d.games}</td>
            <td>${decimalComma(d.score, 1)}</td>
            <td>${decimalComma(d.expected, 2)}</td>
            <td>${d.k === null ? '-' : d.k}</td>
            <td>${d.before || '<span style="color: var(--text-muted);">-</span>'}</td>
            <td>${formatChange(d.change)}</td>
            <td><strong>${d.after}</strong></td>
        </tr>`).join('');
}

// The preview may be opened for a database other than the one on screen, so the
// name lookup uses whichever player list belongs to the database being reported.
let previewPlayers = [];
function activePlayersFor(database) {
    if (activeDb && database && activeDb.id === database.id) return activePlayers;
    return previewPlayers;
}

async function applyPendingReport() {
    if (!pendingReport) return;
    const button = $('btn-report-apply');
    button.setAttribute('disabled', 'disabled');

    const result = await callFunction('applyRatingReport', pendingReport);
    button.removeAttribute('disabled');

    if (!result.ok) {
        window.Dialog.error(describeReportError(result), tr('Could Not Report'));
        return;
    }

    closeModal('report-preview-modal');
    window.Dialog.success(
        tr('{count} ratings updated.', { count: (result.data && result.data.applied) || 0 }),
        tr('Reported')
    );

    // The ratings just moved, so anything cached from before is stale.
    await PlayerDB.clearCachedPlayers(pendingReport.dbId);
    if (activeDb && activeDb.id === pendingReport.dbId) {
        await refreshPlayers({ force: true });
        if (activeTab === 'history') await renderEvents();
    }
    pendingReport = null;
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

export function initPlayerDbPage() {
    $('btn-new-database')?.addEventListener('click', () => { activeDb = null; openDatabaseSettings(null); });
    $('btn-db-back')?.addEventListener('click', async () => { showListPane(); await renderDatabasesPage(); });
    $('btn-db-settings')?.addEventListener('click', () => openDatabaseSettings(activeDb));
    $('db-settings-form')?.addEventListener('submit', saveDatabaseSettings);
    $('btn-db-delete')?.addEventListener('click', deleteActiveDatabase);

    $('db-cards')?.addEventListener('click', (e) => {
        const answer = e.target.closest('[data-db-answer]');
        if (answer) { onRegistryAnswerClick(answer); return; }
        const card = e.target.closest('.db-card');
        // An invitation cannot be opened before it is accepted.
        if (card && !card.classList.contains('db-card-pending')) openDatabase(card.getAttribute('data-db-id'));
    });
    $('btn-db-invite')?.addEventListener('click', inviteToDatabase);
    $('db-invite-email')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); inviteToDatabase(); }
    });
    $('db-arbiters-list')?.addEventListener('click', onSharingListClick);

    document.querySelectorAll('[data-db-tab]').forEach(btn => {
        btn.addEventListener('click', () => selectTab(btn.getAttribute('data-db-tab')));
    });

    $('db-player-search')?.addEventListener('input', (e) => {
        playerFilter = e.target.value.trim();
        renderPlayerTable();
    });
    $('db-player-table-header')?.addEventListener('click', (e) => {
        const th = e.target.closest('th.sortable');
        if (th) handleDbPlayerSort(th.dataset.sort);
    });
    $('db-rating-column')?.addEventListener('change', (e) => {
        ratingColumnListId = e.target.value;
        renderPlayerTable();
    });

    $('btn-db-add-player')?.addEventListener('click', () => openPlayerModal(null));
    $('db-player-form')?.addEventListener('submit', savePlayer);
    $('db-players-list')?.addEventListener('click', (e) => {
        const row = e.target.closest('tr[data-player-id]');
        if (!row) return;
        const playerId = row.getAttribute('data-player-id');
        if (e.target.closest('.db-player-edit')) {
            openPlayerModal(activePlayers.find(p => String(p.id) === String(playerId)));
        } else if (e.target.closest('.db-player-delete')) {
            removePlayer(playerId);
        }
    });

    $('btn-db-add-list')?.addEventListener('click', () => openRatingListModal(null));
    $('rating-list-form')?.addEventListener('submit', saveRatingList);
    $('btn-rl-delete')?.addEventListener('click', deleteRatingListConfig);
    $('btn-rl-add-band')?.addEventListener('click', () => {
        $('rl-k-rows').insertAdjacentHTML('beforeend', kBandRow(9999, 20));
    });
    $('rl-k-rows')?.addEventListener('click', (e) => {
        if (e.target.closest('.rl-k-remove')) e.target.closest('.rl-k-row').remove();
    });
    $('db-lists-container')?.addEventListener('click', (e) => {
        const card = e.target.closest('[data-list-id]');
        if (card && e.target.closest('.db-list-edit')) openRatingListModal(card.getAttribute('data-list-id'));
    });

    $('db-events-container')?.addEventListener('click', (e) => {
        const card = e.target.closest('[data-event-id]');
        if (card && e.target.closest('.db-event-revert')) revertEvent(card.getAttribute('data-event-id'));
    });

    $('btn-db-import')?.addEventListener('click', openImportModal);
    $('db-import-source')?.addEventListener('change', syncImportSourceVisibility);
    $('btn-db-import-run')?.addEventListener('click', runImport);

    $('btn-report-apply')?.addEventListener('click', applyPendingReport);

    wireFideSearch();
}

// Exposed for the tournament form, which needs the arbiter's databases to offer a
// rating list to bind a group to.
// ---------------------------------------------------------------------------
// Databases shared by someone else: answering the invitation, and reacting to
// sharing notifications as they arrive.
// ---------------------------------------------------------------------------

const seenShareNotifications = new Set();

function databasesViewVisible() {
    const view = document.getElementById('databases');
    return !!(view && view.classList.contains('active'));
}

/**
 * Accept or decline an invitation to a database, then refresh the registry so it
 * moves under "Shared with me" or disappears. Used by the card buttons here and
 * by the notification bell in js/app.js, which shows its own dialogs.
 */
export async function answerDatabaseInvite(dbId, accept) {
    if (accept) await PlayerDB.acceptDatabaseInvite(dbId);
    else await PlayerDB.declineDatabaseInvite(dbId);
    if (databasesViewVisible() && !activeDb) await renderDatabasesPage();
}

async function onRegistryAnswerClick(btn) {
    const card = btn.closest('.db-card');
    if (!card) return;
    const accept = btn.getAttribute('data-db-answer') === 'accept';
    const buttons = card.querySelectorAll('[data-db-answer]');
    buttons.forEach(b => { b.disabled = true; });
    try {
        await answerDatabaseInvite(card.getAttribute('data-db-id'), accept);
        if (!accept) window.Dialog.success(tr('You declined the invitation.'), tr('Declined'));
    } catch (err) {
        console.error('Could not answer the database invitation:', err);
        buttons.forEach(b => { b.disabled = false; });
        window.Dialog.error(tr('Could not answer the invitation. It may have been retracted by the owner.'), tr('Error'));
        await renderDatabasesPage();
    }
}

/**
 * React to sharing notifications (called from the notification listener in
 * js/app.js on every snapshot). Only NEW ones count: a "you were removed"
 * notification sits in the bell until it is dismissed, and must not act again on
 * every later snapshot - least of all on a database the arbiter has since been
 * invited back into.
 */
export function handleDatabaseShareNotifications(notifications) {
    const { fresh, lost, sharingChanged } = classifyShareNotifications(
        notifications, seenShareNotifications,
        {
            activeDbId: activeDb ? activeDb.id : '',
            isOwner: !!activeDb && activeDb.ownerId === currentUid()
        });
    if (fresh.length === 0) return;
    fresh.forEach(n => seenShareNotifications.add(n.id));

    // The database on screen was taken away: leave it, rather than letting the
    // next read or write fail with a permission error.
    if (lost) {
        closeModal('db-settings-modal');
        showListPane();
        window.Dialog.alert(tr('This database is no longer shared with you.'), tr('Player Databases'));
    }

    // An owner with the sharing list open sees an answer land without reopening it.
    if (sharingChanged) refreshSharing();

    if (databasesViewVisible() && !activeDb) renderDatabasesPage();
}

/**
 * The arbiter's databases and the rating lists inside each, for the tournament
 * settings picker.
 *
 * Every database is returned, INCLUDING one with no rating lists. It used to
 * enumerate lists and nothing else, so a database without one could not be
 * reached from the picker at all — which made an unrated register of players
 * impossible to attach to a tournament, and forced an arbiter to invent a rating
 * list they did not want just to use their own player list. Binding a database
 * alone is enough for everything that is not rating: the Add Player search reads
 * it (js/ui/player-search.js keys off ratingDbId, not the list) and players added
 * from the roster link back to it.
 */
export async function getSelectableDatabases() {
    try {
        return toSelectableDatabases(await PlayerDB.listMyDatabases());
    } catch (e) {
        console.error('Could not load player databases:', e);
        return [];
    }
}
